import { useParams } from 'wouter';
import { HeadingName } from '../shared/heading-name';
import { useState } from 'react';
import { SidebarHeader } from '../shared/sidebar-header';
import { Parameter } from '../shared/parameter';
import { format_date, format_percentage } from '../shared/formatters';
import { ExpectedTime } from '../shared/expected-time';
import { Divider } from '../shared/divider';
import { ArrowLink } from '../shared/arrow-link';
import clsx from 'clsx';
import { ExpandedSidebar } from '../shared/expanded-sidebar';
import { Timer } from '../shared/timer';
import { WasteValue } from '../shared/waste-value';
import { useMachineStatus } from '../hooks/use-machine-status';
import { ResLoading } from '../shared/res-loading';
import { Alarm } from '../api-new';

export const Workstation = () => {
  const [expanded, setExpanded] = useState(false);
  const { workstation_id } = useParams<{ workstation_id: string }>();
  const { data: line, isLoading, error } = useMachineStatus();

  if (!line || isLoading) return <ResLoading />;
  if (error) return <div>Error: {error.message}</div>;

  const w_info = line.workcenter_info.subcenters.find(
    (w) => w.sub_ws_id === Number(workstation_id),
  );

  const w_index = line.current.stations.findIndex(
    (s) => s.center_id === Number(workstation_id),
  );
  const w = line.current.stations[w_index];

  if (!w || !w_info) return <div>Workstation not found</div>;

  const warnings = w.alarms.filter((a) => a.type === 'WARNING');
  const errors = w.alarms.filter((a) => a.type === 'ALARM');
  const stations = line.current.stations;
  const totalStations = stations.length;

  const prev_w = stations[(w_index - 1 + totalStations) % totalStations];
  const next_w = stations[(w_index + 1) % totalStations];

  const status = w.status_code;

  return (
    <div
      className="absolute z-10 h-full w-1/3 top-0 left-0 bg-white/90 border-r-2 border-neutral-500 border-opacity-30 p-6 flex flex-col gap-4"
      onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between border-b-2 pb-4">
        <ArrowLink id={String(prev_w.center_id)} direction="prev" />
        <HeadingName
          name={w_info.sub_ws_name}
          status={status}
          started_at={w.state_start_unix_timestamp}
          size="small"
        />
        <ArrowLink id={String(next_w.center_id)} direction="next" />
      </div>
      <SidebarHeader
        title="Parametri Stazione"
        expanded={expanded}
        onExpand={() => setExpanded((prev) => !prev)}
      />
      <div className="relative w-full flex-grow flex flex-col justify-around">
        <Parameter label="Ordine corrente" value={w.order} />
        <Divider />
        <Parameter label="Progr. saldatura" value={w.program} />
        <Divider />
        <Parameter label="Codice articolo" value={w.article} />
        <Divider />
        <Parameter label="Descr. articolo" value={w.article_description} />
        <Divider />
        <Parameter
          label="Tempo atteso completamento"
          value={<ExpectedTime end_by={w.expected_end_time} />}
          unit="(hh:mm:ss)"
          size="l"
        />
        <Divider />
        <Parameter
          label="Buoni / Previsti"
          value={
            <span className="w-full flex gap-2 justify-end items-end">
              <span className="text-green-500 text-4xl font-bold">
                {w.goods}
              </span>
              <span className="text-neutral-500 font-thin text-4xl">/</span>
              <span className="text-black text-4xl font-bold">
                {w.expected_goods}
              </span>
            </span>
          }
          unit={
            <span>
              <b>{format_percentage(w.goods / w.expected_goods)}</b>{' '}
              completamento
            </span>
          }
          size="l"
        />
        <Divider />
        <Parameter
          label="Scarti"
          value={<WasteValue value={w.wastes} />}
          size="l"
        />
        <Divider />
        {/* <Parameter
          label="OEE Stazione"
          value={
            <span className="text-green-500">{format_percentage(w.oee)}</span>
          }
          size="l"
        />
        <Divider /> */}
        <Parameter
          label="Consumo Stazione"
          value={w.energy}
          unit="(kWh)"
          size="l"
        />
        <div>
          <div className="mb-4 flex items-center gap-2 text-neutral-500">
            <span
              className={clsx(
                warnings.length > 0 && 'text-yellow-500 font-bold',
              )}>
              {warnings.length} Warning
            </span>
            <span className="text-neutral-400">/</span>
            <span
              className={clsx(errors.length > 0 && 'text-red-500 font-bold')}>
              {errors.length} Errori
            </span>
          </div>
          <div className="flex flex-col gap-2 pr-2 max-h-36 overflow-auto">
            {errors.map((error, i) => (
              <IssuePreview
                key={i}
                colors="border-red-500 bg-red-100 text-red-600"
                issue={error}
              />
            ))}
            {warnings.map((error, i) => (
              <IssuePreview
                key={i}
                colors="border-yellow-500 bg-yellow-100 text-yellow-600"
                issue={error}
              />
            ))}
          </div>
        </div>
      </div>
      <ExpandedSidebar open={expanded} secondary parameters={w.parameters} />
    </div>
  );
};

const IssuePreview = (ps: { colors: string; issue: Alarm }) => (
  <div
    className={clsx(
      'rounded-xl py-2 px-4 border-2 flex items-center justify-between',
      ps.colors,
    )}>
    <div className="flex flex-col justify-between">
      <small className="text-xs font-light">
        {format_date(ps.issue.unix_start)}
      </small>
      <span className="text-sm font-bold">{ps.issue.text}</span>
    </div>
    <span className="font-bold">
      <Timer start={ps.issue.unix_start} />
    </span>
  </div>
);
